import { ChevronRight, Undo2 } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useState } from "react";
import type { LayoutChangeEvent, LayoutRectangle } from "react-native";
import Animated, {
  Easing,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { View, XStack, YStack } from "tamagui";
import type BaseModuleModel from "../../modules/core/BaseModuleModel";
import type { IconProp } from "../../ui/icons";
import { Label } from "../../ui/Label";
import { Subtext } from "../../ui/Subtext";

const DoneStep: React.FC<{
  id: string;
  title: string;
  description: string;
  icon: IconProp;
  onPress?: () => void;
  onLayout?: ((event: LayoutChangeEvent) => void) | undefined;
  active?: boolean;
  noAction?: boolean;
}> = observer((props) => {
  const Icon = props.icon;
  return (
    <View
      id={props.id}
      onLayout={props.onLayout}
      onPress={props.onPress}
      cursor={props.onPress ? "pointer" : undefined}
    >
      <XStack
        backgroundColor={props.noAction ? "$divider" : "$primary"}
        paddingHorizontal={"$larger"}
        paddingVertical={"$medium"}
        gap={"$medium"}
        alignItems="center"
      >
        <View width={24}>
          <Icon />
        </View>
        <YStack flex={1}>
          <Label>{props.title}</Label>
          <Subtext>{props.description}</Subtext>
        </YStack>
        {props.onPress && (
          <View width={24}>
            <ChevronRight />
          </View>
        )}
      </XStack>
    </View>
  );
});

export type DoneStep = {
  icon: IconProp;
  title: string;
  description: string;
  id: string;
  noAction?: boolean;
  skip?: boolean;
  onPress?: () => void;
};

export const DoneSteps: React.FC<{
  // biome-ignore lint/suspicious/noExplicitAny: Because
  module: BaseModuleModel<any, any>;
  steps: DoneStep[];
  activeId: string;
}> = observer((props) => {
  const height = useSharedValue(50);
  const [itemsLayout, setItemsLayout] = useState<LayoutRectangle[]>([]);
  const translateY = useSharedValue(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeSteps = props.steps.filter((step) => !step.skip);

  useEffect(() => {
    setActiveIndex(activeSteps.findIndex((step) => step.id === props.activeId));
  }, [props.activeId, activeSteps]);

  const onLayout = useCallback((index: number, event: LayoutChangeEvent) => {
    setItemsLayout((current) => {
      const newArray = [...current];

      newArray[index] = event.nativeEvent.layout;

      return newArray;
    });
  }, []);

  useEffect(() => {
    const itemLayout = itemsLayout[activeIndex];

    if (!itemLayout) {
      return;
    }

    let newTranslateY = 0;

    for (let i = 0; i < activeIndex; i++) {
      const itemLayout = itemsLayout[i];
      if (itemLayout) {
        newTranslateY += itemLayout.height;
      }
    }

    height.value = withTiming(itemLayout?.height, {
      easing: Easing.inOut(Easing.poly(4)),
      duration: 1500,
    });
    translateY.value = withTiming(-newTranslateY, {
      easing: Easing.inOut(Easing.poly(4)),
      duration: 1500,
    });
  }, [activeIndex, itemsLayout, height, translateY]);

  if (props.module.isDone) {
    return (
      <DoneStep
        id="revert"
        icon={Undo2}
        title="You're done!"
        description="If you want to change your selection, click here to go back"
        onPress={() => props.module.toggleDoneState()}
        noAction
      />
    );
  }

  return (
    <Animated.View
      style={{
        position: "sticky" as "relative",
        bottom: 0,
        zIndex: 100,
        height,
        overflow: "hidden",
      }}
    >
      <Animated.View
        style={{ position: "relative", flex: 1, transform: [{ translateY }] }}
      >
        {activeSteps.map((step, index) => (
          <DoneStep
            key={step.id}
            {...step}
            onLayout={(event) => onLayout(index, event)}
          />
        ))}
      </Animated.View>
    </Animated.View>
  );
});
